import React from "react";
import PropTypes from "prop-types";

/**
 * Account avatar for the preview cards, with initials when the image fails.
 *
 * Previously each component handled this inline with
 * `e.target.parentElement.innerHTML = placeholder`, which replaced the *whole
 * header* — the element holding the avatar also holds the account name and the
 * timestamp. Providers hand out signed avatar URLs that expire (fbcdn and
 * cdninstagram answer 403 on stale ones), so a perfectly normal preview lost
 * its identity line and looked unfinished (OP#2505).
 *
 * @param {Object} props - Component props.
 * @param {string} props.src - Avatar URL.
 * @param {string} props.name - Account name, for the initial.
 * @param {string} props.background - Fallback circle colour.
 * @param {string|number} props.size - Fallback circle size (CSS length).
 * @param {string|number} props.fontSize - Initial's font size.
 * @param {string} props.className - Passed to the image.
 * @param {Object} props.style - Passed to the image.
 * @param {Object} props.fallbackStyle - Extra styles for the fallback circle.
 * @returns {JSX.Element} The avatar image, or its initials on failure.
 */
export default function AvatarFallback({
  src,
  name,
  background,
  size,
  fontSize,
  className,
  style,
  fallbackStyle,
}) {
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    setFailed(false);
  }, [src]);

  const initial = (name || "").trim().charAt(0).toUpperCase() || "?";

  if (!src || failed) {
    return (
      <div
        style={{
          width: size,
          height: size,
          // Every call site puts this in a flex row, where the default
          // flex-shrink squeezed the circle to a 14px sliver.
          flexShrink: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontWeight: "bold",
          fontSize,
          borderRadius: "50%",
          background,
          ...fallbackStyle,
        }}>
        {initial}
      </div>
    );
  }

  return (
    <img
      src={src}
      alt=""
      className={className}
      style={style}
      onError={() => setFailed(true)}
    />
  );
}

AvatarFallback.propTypes = {
  src: PropTypes.string,
  name: PropTypes.string,
  background: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
  style: PropTypes.object,
  fallbackStyle: PropTypes.object,
};

AvatarFallback.defaultProps = {
  src: "",
  name: "",
  background: "#8a8d91",
  size: "50px",
  fontSize: "20px",
  className: "",
  style: {},
  fallbackStyle: {},
};
